import { isAxiosError } from "axios";

import { PillVariants } from "src/base-components/Pill";
import { CaseDeserialized } from "src/cases/queries/casesTypes";
import { CasePropertiesValue } from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { isPreconditionError } from "src/utils/predicates";

type OrderedProperty = {
  key: string;
  schema: CasePropertiesValue;
  value: unknown;
};

/**
 * Case-instance properties that have a schema entry and a value, resolved
 * and ordered by the schema's `order` field. Object/dict key order has no
 * round-trip guarantee, so display order must come from this explicit field.
 */
export const getOrderedProperties = (
  schemaProperties: Record<string, CasePropertiesValue>,
  caseProperties: Record<string, unknown>,
): OrderedProperty[] => {
  return Object.keys(schemaProperties)
    .filter((key) => key in caseProperties)
    .map((key) => ({
      key,
      schema: schemaProperties[key],
      value: caseProperties[key],
    }))
    .sort((a, b) => (a.schema.order ?? 0) - (b.schema.order ?? 0));
};

export const getPropertyVariant = (
  value: string,
  property?: CasePropertiesValue,
): PillVariants => {
  if (!value || !property) return "gray";

  if (property.type === "enum") {
    const enumValue = property.values?.find((v) => v.value === value);
    if (enumValue?.color) {
      return enumValue.color as PillVariants;
    }
  }
  return "gray";
};

export const getValueDisplayName = (
  value: string,
  property?: CasePropertiesValue,
): string => {
  if (!property) return value;
  if (property.type === "enum" || property.type === "tags") {
    return (
      property.values?.find((v) => v.value === value)?.display_name || value
    );
  }
  return value;
};

export const getStatusVariant = (
  status: string,
  templateData?: CaseDeserialized["data"]["template_version"],
): PillVariants => {
  return getPropertyVariant(status, templateData?.case_properties?.status);
};

export const getStatusDisplayName = (
  status: string,
  templateData?: CaseDeserialized["data"]["template_version"],
): string => {
  return getValueDisplayName(status, templateData?.case_properties?.status);
};

export const getSubValueDisplayName = (
  parentValue: string,
  subValue: string | undefined | null,
  property?: CasePropertiesValue,
): string | undefined => {
  if (!subValue || !property || property.type !== "enum") return undefined;
  const enumValue = property.values?.find((v) => v.value === parentValue);
  return (
    enumValue?.sub_values?.find((sv) => sv.value === subValue)?.display_name ||
    undefined
  );
};

export const getSubStatusDisplayName = (
  status: string,
  subStatus: string,
  templateData?: CaseDeserialized["data"]["template_version"],
): string => {
  return (
    getSubValueDisplayName(
      status,
      subStatus,
      templateData?.case_properties?.status,
    ) || subStatus
  );
};

export const getPriorityVariant = (
  priority: string,
  templateData?: CaseDeserialized["data"]["template_version"],
): PillVariants => {
  return getPropertyVariant(priority, templateData?.case_properties?.priority);
};

export const getPriorityDisplayName = (
  priority: string,
  templateData?: CaseDeserialized["data"]["template_version"],
): string => {
  return getValueDisplayName(priority, templateData?.case_properties?.priority);
};

export type PropertyDisplay = {
  variant: PillVariants;
  displayName: string;
};

export const getPropertyDisplay = (
  value: string,
  property?: CasePropertiesValue,
): PropertyDisplay => ({
  variant: getPropertyVariant(value, property),
  displayName: getValueDisplayName(value, property),
});

export const getStatusDisplay = (
  status: string,
  templateData?: CaseDeserialized["data"]["template_version"],
): PropertyDisplay => ({
  variant: getStatusVariant(status, templateData),
  displayName: getStatusDisplayName(status, templateData),
});

export const getPriorityDisplay = (
  priority: string,
  templateData?: CaseDeserialized["data"]["template_version"],
): PropertyDisplay => ({
  variant: getPriorityVariant(priority, templateData),
  displayName: getPriorityDisplayName(priority, templateData),
});

export const onAssignCasesError = (
  error: unknown,
  toastOptions: {
    id: string;
    action: "claiming" | "assigning";
  },
) => {
  if (isAxiosError(error)) {
    toastActions.failure({
      id: toastOptions.id,
      title: `Error ${toastOptions.action} cases`,
      description: error.response?.data.errors[0]?.detail ?? "Unknown error",
    });
  } else {
    toastActions.failure({
      id: toastOptions.id,
      title: `Error ${toastOptions.action} cases`,
      description: `An error occurred while ${toastOptions.action} the cases. Please try again later.`,
    });
  }
};

export const onArchiveCasesError = (
  error: unknown,
  toastOptions?: { id?: string },
) => {
  if (isPreconditionError(error)) {
    toastActions.failure({
      id: toastOptions?.id,
      title: "Archiving failed",
      description:
        "An error occurred while archiving. Please refresh the page and try again.",
    });
    return;
  }

  if (isAxiosError(error)) {
    toastActions.failure({
      id: toastOptions?.id,
      title: "Archiving failed",
      description: error.response?.data.errors[0]?.detail ?? "Unknown error",
    });
    return;
  }

  toastActions.failure({
    id: toastOptions?.id,
    title: "Archiving failed",
    description:
      "Unexpected error occurred while archiving. Please try again later.",
  });
};

/**
 *
 * Type predicate to check if a property is the status property
 * @param property - property to check
 */
export const isStatusProperty = (
  property?: CasePropertiesValue,
): property is Extract<CasePropertiesValue, { type: "enum"; key: "status" }> =>
  !!property &&
  "key" in property &&
  property.key === "status" &&
  property.type === "enum";
